/**
 * Support Guide: Understand the Scoring Models
 * Category: Scoring (yellow #eab308)
 *
 * 450 frames @ 30fps = 15 seconds
 *
 * Flow:
 *  0-45      Title card
 *  45-120    Model 1 — Best Score (Max)
 *  120-195   Model 2 — Average
 *  195-270   Model 3 — Total (Sum)
 *  270-350   Model 4 — Olympic (Drop High & Low)
 *  350-420   Model 5 — Weighted Average
 *  420-450   Summary card — "Pick the right one for your event"
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, Easing } from "remotion";
import { SlideIn } from "../../components/SlideIn";
import { FadeTransition } from "../../components/FadeTransition";
import { TypeWriter } from "../../components/TypeWriter";

const ACCENT = "#eab308";

interface ModelData {
  num: number;
  name: string;
  color: string;
  subtitle: string;
  judgeScores: number[];
  formula: string;
  result: string;
  droppedIndices?: number[];
  weightedIndex?: number;
}

const MODELS: ModelData[] = [
  {
    num: 1,
    name: "Best Score (Max)",
    color: "#3b82f6",
    subtitle: "Rider keeps only their highest judge score",
    judgeScores: [7.0, 8.5, 6.0, 9.0, 7.5],
    formula: "max(7.0, 8.5, 6.0, 9.0, 7.5)",
    result: "9.0",
  },
  {
    num: 2,
    name: "Average",
    color: "#22c55e",
    subtitle: "Mean of all judge scores — standard for most events",
    judgeScores: [8.0, 8.5, 7.5, 9.0, 8.0],
    formula: "(8.0 + 8.5 + 7.5 + 9.0 + 8.0) / 5",
    result: "8.2",
  },
  {
    num: 3,
    name: "Total (Sum)",
    color: "#a855f7",
    subtitle: "All scores added together",
    judgeScores: [8.0, 7.5, 8.5, 9.0, 8.0],
    formula: "8.0 + 7.5 + 8.5 + 9.0 + 8.0",
    result: "41.0",
  },
  {
    num: 4,
    name: "Olympic (Drop High & Low)",
    color: "#ef4444",
    subtitle: "Remove the highest and lowest, average the rest",
    judgeScores: [7.0, 8.5, 6.0, 9.0, 8.0],
    formula: "drop 6.0 & 9.0 → avg(7.0, 8.5, 8.0)",
    result: "7.8",
    droppedIndices: [2, 3], // indices of 6.0 and 9.0
  },
  {
    num: 5,
    name: "Weighted",
    color: ACCENT,
    subtitle: "Head judge (Judge #1) counts double",
    judgeScores: [9.0, 8.0, 7.5, 8.5, 8.0],
    formula: "(9.0×2 + 8.0 + 7.5 + 8.5 + 8.0) / 6",
    result: "8.5",
    weightedIndex: 0,
  },
];

export function ScoringModelsGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      {/* Title card: 0-45 */}
      <Sequence durationInFrames={45}>
        <FadeTransition durationInFrames={45} fadeIn={10} fadeOut={8}>
          <TitleCard />
        </FadeTransition>
      </Sequence>

      {/* Model 1 — Max: 45-120 (75 frames) */}
      <Sequence from={45} durationInFrames={75}>
        <FadeTransition durationInFrames={75} fadeIn={8} fadeOut={8}>
          <ModelCard model={MODELS[0]} totalFrames={75} />
        </FadeTransition>
      </Sequence>

      {/* Model 2 — Average: 120-195 (75 frames) */}
      <Sequence from={120} durationInFrames={75}>
        <FadeTransition durationInFrames={75} fadeIn={8} fadeOut={8}>
          <ModelCard model={MODELS[1]} totalFrames={75} />
        </FadeTransition>
      </Sequence>

      {/* Model 3 — Sum: 195-270 (75 frames) */}
      <Sequence from={195} durationInFrames={75}>
        <FadeTransition durationInFrames={75} fadeIn={8} fadeOut={8}>
          <ModelCard model={MODELS[2]} totalFrames={75} />
        </FadeTransition>
      </Sequence>

      {/* Model 4 — Olympic: 270-350 (80 frames) */}
      <Sequence from={270} durationInFrames={80}>
        <FadeTransition durationInFrames={80} fadeIn={8} fadeOut={8}>
          <ModelCard model={MODELS[3]} totalFrames={80} />
        </FadeTransition>
      </Sequence>

      {/* Model 5 — Weighted: 350-420 (70 frames) */}
      <Sequence from={350} durationInFrames={70}>
        <FadeTransition durationInFrames={70} fadeIn={8} fadeOut={8}>
          <ModelCard model={MODELS[4]} totalFrames={70} />
        </FadeTransition>
      </Sequence>

      {/* Summary: 420-450 (30 frames) */}
      <Sequence from={420} durationInFrames={30}>
        <FadeTransition durationInFrames={30} fadeIn={8} fadeOut={6}>
          <SummaryCard />
        </FadeTransition>
      </Sequence>
    </AbsoluteFill>
  );
}

function TitleCard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        justifyContent: "center",
        alignItems: "center",
        opacity,
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontSize: 14,
            color: ACCENT,
            textTransform: "uppercase",
            letterSpacing: 4,
            fontFamily: "'JetBrains Mono', monospace",
            marginBottom: 20,
          }}
        >
          Support Guide
        </div>
        <div
          style={{
            fontSize: 64,
            fontWeight: 800,
            color: "#fff",
            maxWidth: 900,
            lineHeight: 1.1,
          }}
        >
          Understand the Scoring Models
        </div>
        <div style={{ marginTop: 24 }}>
          <TypeWriter
            text="5 models · visual explainer"
            delay={10}
            speed={1}
            style={{ fontSize: 18, color: "#94a3b8" }}
          />
        </div>
      </div>
    </AbsoluteFill>
  );
}

function ModelCard({ model, totalFrames }: { model: ModelData; totalFrames: number }) {
  const frame = useCurrentFrame();
  const { num, name, color, subtitle, judgeScores, formula, result, droppedIndices, weightedIndex } = model;

  // Title animates in early
  const titleOpacity = interpolate(frame, [0, 10], [0, 1], { extrapolateRight: "clamp" });
  const titleY = interpolate(frame, [0, 10], [20, 0], {
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.ease),
  });

  // Each judge score appears one by one starting at frame 8
  const scoreStart = 8;
  const scoreStagger = 4; // frames between each score appearing

  // Formula appears after all scores
  const formulaStart = scoreStart + judgeScores.length * scoreStagger + 6;
  const formulaOpacity = interpolate(frame, [formulaStart, formulaStart + 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Result appears last with scale-up
  const resultStart = formulaStart + 10;
  const resultProgress = interpolate(frame, [resultStart, resultStart + 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.back(1.5)),
  });
  const resultOpacity = resultProgress;

  // Background glow pulse
  const glowOpacity = interpolate(
    frame,
    [resultStart, resultStart + 10, totalFrames],
    [0, 0.08, 0.04],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  return (
    <AbsoluteFill
      style={{
        justifyContent: "center",
        alignItems: "center",
        background: `radial-gradient(ellipse at center, ${color}${Math.round(glowOpacity * 255)
          .toString(16)
          .padStart(2, "0")} 0%, transparent 70%)`,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 1200 }}>
        {/* Model number badge */}
        <div
          style={{
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontSize: 13,
              color,
              textTransform: "uppercase",
              letterSpacing: 3,
              fontFamily: "'JetBrains Mono', monospace",
              padding: "4px 16px",
              border: `1px solid ${color}`,
              borderRadius: 4,
            }}
          >
            Model {num} of 5
          </span>
        </div>

        {/* Model name */}
        <div
          style={{
            fontSize: 56,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.1,
            marginBottom: 10,
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
          }}
        >
          {name}
        </div>

        {/* Subtitle */}
        <div
          style={{
            fontSize: 20,
            color: "#94a3b8",
            marginBottom: 48,
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
          }}
        >
          {subtitle}
        </div>

        {/* Judge score boxes */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 24,
            marginBottom: 36,
          }}
        >
          {judgeScores.map((score, i) => {
            const isDropped = droppedIndices?.includes(i) ?? false;
            const isWeighted = weightedIndex === i;
            const scoreAppear = interpolate(
              frame,
              [scoreStart + i * scoreStagger, scoreStart + i * scoreStagger + 6],
              [0, 1],
              { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
            );
            // For dropped scores, show strikethrough after all scores appear
            const dropStart = scoreStart + judgeScores.length * scoreStagger + 2;
            const dropProgress = isDropped
              ? interpolate(frame, [dropStart, dropStart + 6], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                })
              : 0;

            const boxBorderColor = isDropped
              ? interpolate(dropProgress, [0, 1], [1, 0.3])
              : 1;

            return (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  opacity: scoreAppear,
                }}
              >
                {/* Judge label */}
                <div
                  style={{
                    fontSize: 12,
                    color: "#52525b",
                    fontFamily: "'JetBrains Mono', monospace",
                    marginBottom: 8,
                    fontWeight: 600,
                  }}
                >
                  J{i + 1}
                  {isWeighted && (
                    <span style={{ color: ACCENT, fontWeight: 800 }}> ×2</span>
                  )}
                </div>

                {/* Score box */}
                <div
                  style={{
                    width: 90,
                    height: 90,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 32,
                    fontWeight: 800,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: isDropped && dropProgress > 0.5 ? "#52525b" : "#fff",
                    background: isDropped && dropProgress > 0.5 ? "#0a0a0b" : `${color}18`,
                    border: `2px solid ${color}`,
                    borderColor: `rgba(${hexToRgb(color)}, ${boxBorderColor})`,
                    borderRadius: 8,
                    textDecoration: isDropped && dropProgress > 0.5 ? "line-through" : "none",
                    transition: "none",
                    position: "relative",
                  }}
                >
                  {score.toFixed(1)}
                </div>

                {/* Dropped label */}
                {isDropped && dropProgress > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: "#ef4444",
                      marginTop: 6,
                      textTransform: "uppercase",
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 700,
                      letterSpacing: 1,
                      opacity: dropProgress,
                    }}
                  >
                    dropped
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Formula */}
        <div
          style={{
            opacity: formulaOpacity,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: "#52525b",
              textTransform: "uppercase",
              letterSpacing: 2,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: 8,
            }}
          >
            Formula
          </div>
          <div
            style={{
              fontSize: 18,
              color: "#94a3b8",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {formula}
          </div>
        </div>

        {/* Result */}
        <div
          style={{
            opacity: resultOpacity,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: "#52525b",
              textTransform: "uppercase",
              letterSpacing: 2,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: 8,
            }}
          >
            Final Score
          </div>
          <div
            style={{
              fontSize: 80,
              fontWeight: 800,
              color,
              fontFamily: "'JetBrains Mono', monospace",
              lineHeight: 1,
            }}
          >
            {result}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
}

function SummaryCard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 10], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        justifyContent: "center",
        alignItems: "center",
        opacity,
      }}
    >
      <SlideIn duration={10}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 14,
              color: ACCENT,
              textTransform: "uppercase",
              letterSpacing: 3,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: 16,
            }}
          >
            Choose Your Model
          </div>
          <div
            style={{
              fontSize: 44,
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: 20,
            }}
          >
            Pick the right one for your event
          </div>
          <div
            style={{
              fontSize: 16,
              color: "#94a3b8",
              lineHeight: 1.6,
              maxWidth: 600,
              margin: "0 auto 32px",
            }}
          >
            You choose the model when creating the competition. It can be changed in competition settings.
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              flexWrap: "wrap",
              maxWidth: 900,
            }}
          >
            {MODELS.map((m) => (
              <div
                key={m.num}
                style={{
                  padding: "10px 20px",
                  background: `${m.color}12`,
                  border: `1px solid ${m.color}40`,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: m.color,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: m.color,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {m.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </SlideIn>
    </AbsoluteFill>
  );
}

/** Convert hex color to "r, g, b" string for rgba() usage */
function hexToRgb(hex: string): string {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}
